import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, Typogaphy } from 'vnetwork-ui'
import { BODApproveAllForm } from '@/modules/Dayoffs/components/Form/BODApproveAllForm'
import { LeaderApproveAllForm } from '@/modules/Dayoffs/components/Form/LeaderApproveAllForm'
import { DayOffResponse } from '@/modules/Dayoffs/types/response'
import {
    DataTableCustomized,
    createColumns,
} from '@/components/ui/data-table-customized'
import { ModalProps } from '@/types/shared'
import { Badge } from '@/components/ui/badge'

export interface ApproveMultipleDayOffModalProps extends ModalProps {
    ids: number[]
    rows?: DayOffResponse[]
    refetch: () => void
}

const ApproveMultipleDayOffModal: React.FC<ApproveMultipleDayOffModalProps> = ({
    onClose,
    open,
    ids,
    rows,
    refetch,
}) => {
    const { t } = useTranslation()
    const [previewPage, setPreviewPage] = useState(1)
    const [previewPageSize, setPreviewPageSize] = useState(10)

    // Calculate counts by status
    const statusCounts = useMemo(() => {
        if (!rows) return { pending: 0, leaderApproved: 0 }
        return {
            pending: rows.filter((r) => r.day_off_status === 'pending').length,
            leaderApproved: rows.filter(
                (r) => r.day_off_status === 'leader-approved'
            ).length,
        }
    }, [rows])

    // Preview columns for the table
    const previewColumns = createColumns<DayOffResponse>([
        {
            key: 'day_off_id',
            title: 'ID',
            width: 70,
        },
        {
            key: 'employee_name',
            title: t('employee_name'),
            width: 150,
        },
        {
            key: 'department_name',
            title: t('department_name'),
            width: 130,
        },
        {
            key: 'position_name',
            title: t('position'),
            width: 120,
        },
        {
            key: 'number_of_day_off',
            title: t('days'),
            width: 60,
            cellClassName: 'text-center',
            headerClassName: 'text-center',
        },
        {
            key: 'day_off_type',
            title: t('day_off_type'),
            width: 100,
            render: (value) => t(value),
        },
        {
            key: 'day_off_status',
            title: t('day_off_status'),
            width: 110,
            render: (value) => {
                const colorClass =
                    value === 'pending' || value === 'leader-approved'
                        ? 'text-orange-500'
                        : value === 'approved'
                          ? 'text-green-600'
                          : 'text-red-600'
                return <span className={colorClass}>{t(value)}</span>
            },
        },
    ])

    // Paginate rows
    const startIndex = (previewPage - 1) * previewPageSize
    const paginatedRows =
        rows?.slice(startIndex, startIndex + previewPageSize) || []

    return (
        <Dialog
            onClose={onClose}
            open={open}
            title={t('dayoffs.multiple-approve')}
            className="max-h-[90vh] overflow-y-auto !max-w-5xl"
        >
            {/* Summary badges */}
            <div className="flex items-center gap-3 mb-3">
                <Typogaphy className="text-sm text-muted-foreground">
                    {t('selected_dayoff')}: <strong>{ids.length}</strong>{' '}
                    {t('items')}
                </Typogaphy>
                <div className="flex items-center gap-2">
                    <Badge
                        variant="outline"
                        className="bg-orange-50 text-orange-600 border-orange-200"
                    >
                        {t('pending')}: {statusCounts.pending}
                    </Badge>
                    <Badge
                        variant="outline"
                        className="bg-blue-50 text-blue-600 border-blue-200"
                    >
                        {t('leader-approved')}: {statusCounts.leaderApproved}
                    </Badge>
                </div>
            </div>

            {/* Table Preview */}
            {rows && rows.length > 0 ? (
                <div className="border border-gray-200 rounded-lg mb-4 overflow-hidden">
                    <DataTableCustomized
                        columns={previewColumns}
                        rows={paginatedRows}
                        rowKey={(row) => String(row.day_off_id)}
                        showIndex
                        size="small"
                        emptyMessage={t('no_data')}
                        pagination={{
                            page: previewPage,
                            pageSize: previewPageSize,
                            total: rows.length,
                            pageSizeOptions: [10, 20, 50],
                            onPageChange: setPreviewPage,
                            onPageSizeChange: (size) => {
                                setPreviewPageSize(size)
                                setPreviewPage(1)
                            },
                        }}
                    />
                </div>
            ) : (
                // Fallback to ID list if rows not provided
                <div className="grid grid-cols-1 gap-1 overflow-y-auto border border-gray-100 p-4 rounded-lg my-2 max-h-40 w-full space-y-2">
                    {ids.map((id) => (
                        <Typogaphy
                            key={id}
                            size="small"
                            className="hover:cursor-pointer w-full text-start"
                        >
                            {t('dayoffs.dayoff-id', { dayoffId: id })}
                        </Typogaphy>
                    ))}
                </div>
            )}

            {/* Approval Forms - Vertical layout */}
            <div className="flex flex-col gap-4">
                {/* Leader Approve Section - only pending items */}
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-sm font-semibold text-gray-700">
                            {t('leader_confirm')}
                        </h3>
                        <Badge
                            variant="outline"
                            className="text-xs bg-orange-50 text-orange-600 border-orange-200"
                        >
                            {statusCounts.pending} {t('items')}
                        </Badge>
                    </div>
                    <LeaderApproveAllForm
                        ids={
                            rows
                                ?.filter((r) => r.day_off_status === 'pending')
                                .map((r) => r.day_off_id) || ids
                        }
                        onClose={onClose}
                        refetch={refetch}
                    />
                </div>

                {/* BOD Approve Section - all items (pending + leader-approved) */}
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-sm font-semibold text-gray-700">
                            {t('bod_confirm')}
                        </h3>
                        <Badge
                            variant="outline"
                            className="text-xs bg-blue-50 text-blue-600 border-blue-200"
                        >
                            {ids.length} {t('items')}
                        </Badge>
                    </div>
                    <BODApproveAllForm
                        ids={ids}
                        onClose={onClose}
                        refetch={refetch}
                    />
                </div>
            </div>
        </Dialog>
    )
}

export default ApproveMultipleDayOffModal
